import { Link } from "react-router";
import type { ReactNode } from "react";

/**
 * Actions are pills. The arrow gap widens on hover instead of the whole button
 * moving, a quieter signal than a lift, and it never shifts surrounding layout.
 * Every variant clears the 44px minimum touch target.
 */
const base =
  "group inline-flex min-h-12 items-center justify-center gap-2 rounded-full px-6 text-[0.9375rem] font-semibold transition-[background-color,border-color,color,gap] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] hover:gap-3";

const variants = {
  primary: "bg-navy text-white hover:bg-midnight",
  secondary: "border border-line-strong text-ink hover:border-midnight hover:bg-ivory",
  light: "bg-white text-midnight hover:bg-white/92",
  ghostLight: "border border-white/25 text-white hover:border-white/60 hover:bg-white/10",
} as const;

export type ButtonVariant = keyof typeof variants;

export function ButtonLink({
  href,
  children,
  variant = "primary",
  className = "",
}: {
  href: string;
  children: ReactNode;
  variant?: ButtonVariant;
  className?: string;
}) {
  const classes = `${base} ${variants[variant]} ${className}`;

  if (href.startsWith("http")) {
    return (
      <a href={href} className={classes} target="_blank" rel="noreferrer noopener">
        {children}
      </a>
    );
  }

  return (
    <Link to={href} className={classes}>
      {children}
    </Link>
  );
}

export function ArrowRight({ className = "" }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      fill="none"
      className={`h-3.5 w-3.5 shrink-0 ${className}`}
    >
      <path
        d="M3 8h9M8.5 4.5 12 8l-3.5 3.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * The outbound sibling of ArrowRight: same 16-unit box, same 1.6 stroke, so the
 * two read as one family. Points up and to the right, the convention for a link
 * that leaves the site.
 */
export function ArrowUpRight({ className = "" }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      fill="none"
      className={`h-3.5 w-3.5 shrink-0 ${className}`}
    >
      <path
        d="M4.5 11.5 11.5 4.5M5.75 4.5H11.5V10.25"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Inline text link with an arrow that slides on hover. Used instead of buttons in cards. */
export function TextLink({
  href,
  children,
  tone = "navy",
  className = "",
  stretch = false,
}: {
  href: string;
  children: ReactNode;
  tone?: "navy" | "sky";
  className?: string;
  /** Make a positioned parent (normally a card) clickable through this link. */
  stretch?: boolean;
}) {
  return (
    <Link
      to={href}
      className={`group/link inline-flex min-h-11 items-center gap-2 text-[0.9375rem] font-semibold ${
        tone === "sky" ? "text-sky" : "text-navy"
      } ${className}`}
    >
      <span className={stretch ? "after:absolute after:inset-0 after:content-['']" : undefined}>{children}</span>
      <ArrowRight className="transition-transform duration-400 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-x-1.5" />
    </Link>
  );
}
